import { ReviewDiscoveryListQuery } from "../../../__generated__/graphql";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { HiBolt } from "react-icons/hi2";
import Partition from "../../../components/common/partition";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import Skeleton from "react-loading-skeleton";
import StoreOverviewCard from "./store-overview-card";
import { track } from "@amplitude/analytics-browser";
import clsx from "clsx";
import { formatDateToDateAndTimeObject } from "../../../utils/date/format-date";

interface ReviewItemProps {
  review: ReviewDiscoveryListQuery["reviewDiscoveryList"][number];
  expandedReviews: Set<number>;
  imageLoaded: { [key: number]: boolean };
  onToggleReview: (reviewId: number) => void;
  onImageLoad: (reviewId: number) => void;
  rootRef: React.RefObject<HTMLDivElement>;
}

const ReviewItem = ({
  review,
  expandedReviews,
  imageLoaded,
  onToggleReview,
  onImageLoad,
  rootRef,
}: ReviewItemProps) => {
  const isExpanded = expandedReviews.has(review?.id ?? 0);
  const hasImage = review?.reviewImages && review?.reviewImages.length > 0;
  const contentLength = review?.content?.length || 0;
  const shouldShowExpandButton = contentLength > 60;

  // 리뷰 펼쳐보기 버튼 클릭 시 리뷰 펼쳐짐 상태 변경 함수
  const handleToggleReview = () => {
    track("찐후기 펼쳐보기 클릭");
    onToggleReview(review.id);
  };

  // 이미지 로드 완료 시 이미지 로드 상태 업데이트 함수
  const handleImageLoad = () => {
    onImageLoad(review.id);
  };

  // 리뷰 내용 글자수 60자 이상 처리 (펼쳐보기 버튼 클릭 시 전체 내용 표시)
  const displayContent = isExpanded
    ? review?.content
    : contentLength > 60
    ? `${review?.content?.slice(0, 60)}...`
    : review?.content;

  return (
    <div className="bg-white">
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">
                {review?.user?.maskedName}
              </span>
              {review?.orderType === "PACK" ? (
                <span className="text-md font-medium text-green-600">
                  예약구매
                </span>
              ) : (
                <span className="text-md font-medium text-[#ff4757] flex items-center gap-0.5">
                  <HiBolt className="w-4 h-4" />
                  바로픽업
                </span>
              )}
            </div>
            <div className="text-gray-500 text-md">
              {formatDateToDateAndTimeObject(review?.createdAt).date}{" "}
              {formatDateToDateAndTimeObject(review?.createdAt).time}
            </div>
          </div>
        </div>

        {hasImage && (
          <div className="mb-3">
            <div className="relative w-full aspect-[3/4]">
              {!imageLoaded[review.id] && (
                <div className="absolute inset-0 z-10">
                  <Skeleton className="w-full h-full rounded-lg" />
                </div>
              )}

              <div
                className={clsx(
                  "inset-0 transition-opacity duration-300",
                  !imageLoaded[review.id] ? "opacity-0" : "opacity-100"
                )}
              >
                <ResponsiveCDNImage
                  src={review?.reviewImages[0]?.img}
                  alt="리뷰 이미지"
                  className="w-full object-cover rounded-lg aspect-[3/4]"
                  onLoad={handleImageLoad}
                  lazy
                  root={rootRef}
                  rootMargin="500% 0px 500% 0px"
                />
              </div>

              <div className="absolute bottom-0 left-0 w-full p-3 bg-gradient-to-t from-black/70 to-transparent rounded-lg">
                <StoreOverviewCard review={review} />
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {!hasImage && (
            <div className="border rounded-lg shadow-md">
              <StoreOverviewCard review={review} />
            </div>
          )}
          <div className="text-gray-700">
            <p>{displayContent}</p>
            {shouldShowExpandButton && (
              <button
                onClick={handleToggleReview}
                className="text-blue-600 text-md mt-2 active:text-blue-700 hover:underline"
              >
                {isExpanded ? (
                  <div className="flex items-center gap-0.5">
                    <span>접기</span> <HiChevronUp />
                  </div>
                ) : (
                  <div className="flex items-center gap-0.5">
                    <span>펼쳐보기</span> <HiChevronDown />
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <Partition color="light" height="md" />
    </div>
  );
};

export default ReviewItem;
